import React, { useEffect, useContext } from 'react';
import LayoutContainer from 'components/layout/LayoutContainer';
import AdminHeader from 'components/header/index';
import Card from 'components/inputs/Card';
import { Row, Col } from 'react-bootstrap';
import TextInput from 'components/inputs/Input';
import Select from 'components/inputs/Select';
import Button from 'components/inputs/Button';
import * as _ from 'lodash';
import { Formik, Form, FieldArray, getIn } from 'formik';
import * as Yup from 'yup';
import { useSelector, useDispatch } from 'react-redux';
import { useParams, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { NavContext } from 'components/privateRoute/PrivateRoute';
import { UPDATE_STATUS_CODE, MANAGE_WALLETS } from 'components/common/constant';
import { FETCH_WALLET_TOP_UP_OFFERS, SAVE_WALLET_TOP_UP_OFFERS, FETCH_WALLET_TOP_UP_OFFER_HISTORY } from 'actions/walletTopUpOffer';
import { getOfferId, renderOfferStatus, mutedText, formatOfferDate } from 'view/sessionLevelOffer/SessionLevelOfferList';
import { getTopUpOfferStatus } from 'view/walletTopUpOffer/WalletTopUpOfferList';

const toDateTimeInput = (d) => (d ? moment(d).format('YYYY-MM-DDTHH:mm') : '');
const blankToNull = (v, original) => (original === '' ? null : v);
// blank -> undefined so `required` shows its own message instead of a type error
const numberField = () =>
  Yup.number()
    .transform((v, original) => (original === '' ? undefined : v))
    .typeError('Enter a number');
const DISCOUNT_TYPE_OPTIONS = [
  { label: 'Percentage', value: 'percentage' },
  { label: 'Flat (₹)', value: 'flat' },
];
const TYPE_OPTIONS = [
  { label: 'Cashback', value: 'cashback' },
  { label: 'ChargeCoin', value: 'chargecoin' },
];
// [field, label, col width, input type, required]
const FIELDS = [
  ['discount_value', 'Discount Value', 3, 'number', true],
  ['max_discount', 'Max Discount (₹) (Optional)', 3, 'number'],
  ['min_value', 'Min Amount (₹)', 3, 'number', true],
  ['max_value', 'Max Amount (₹)', 3, 'number', true],
  ['start_date', 'Start Date (Optional, blank = now)', 3, 'datetime-local'],
  ['expire_at', 'Expiry Date', 3, 'datetime-local', true],
  ['priority', 'Priority (Optional, lower first)', 3, 'number'],
  ['content', 'Content', 3, 'text', true],
  ['sub_content', 'Sub Content (Optional)', 3, 'text'],
  ['addon_content', 'Addon Content (Optional)', 3, 'text'],
  ['description', 'Description', 12, 'textarea', true],
];
const slabCard = { border: '1px solid #2a3a48', borderRadius: 8, padding: 16, marginBottom: 16 };

const toSlab = (offer) => ({
  id: getOfferId(offer),
  type: _.get(offer, 'type', 'cashback'),
  min_value: _.get(offer, 'min_value', ''),
  max_value: _.get(offer, 'max_value', ''),
  discount_type: _.get(offer, 'discount_type', 'percentage'),
  discount_value: _.get(offer, 'discount_value', ''),
  max_discount: _.get(offer, 'max_discount') ?? '',
  start_date: toDateTimeInput(_.get(offer, 'start_date')),
  expire_at: toDateTimeInput(_.get(offer, 'expire_at')),
  priority: _.get(offer, 'priority', 1),
  content: _.get(offer, 'content', ''),
  sub_content: _.get(offer, 'sub_content', ''),
  addon_content: _.get(offer, 'addon_content', ''),
  description: _.get(offer, 'description', ''),
});

// new slab copies type, discount type, schedule and priority from the last one
const nextSlab = (last) => ({
  ...toSlab({}),
  ..._.pick(last, ['type', 'discount_type', 'start_date', 'expire_at', 'priority']),
});

const fieldError = (errors, touched, name) => (getIn(errors, name) && getIn(touched, name) ? getIn(errors, name) : null);

const afterStart = (msg) =>
  Yup.date().test('after-start', msg, function (v) {
    return !v || !this.parent.start_date || v > this.parent.start_date;
  });

const validationSchema = Yup.object().shape({
  offers: Yup.array()
    .min(1, 'Add at least one slab')
    .of(
      Yup.object().shape({
        min_value: numberField().min(0, 'Min amount cannot be negative').required('Min amount is required'),
        max_value: numberField().min(0, 'Max amount cannot be negative').required('Max amount is required'),
        discount_value: numberField().min(0, 'Discount cannot be negative').required('Discount is required'),
        start_date: Yup.date().transform(blankToNull).nullable(),
        expire_at: afterStart('Expiry must be after the start date').required('Expiry date is required'),
        max_discount: numberField().transform(blankToNull).min(0, 'Max discount cannot be negative').nullable(),
        priority: numberField().transform(blankToNull).min(0, 'Priority cannot be negative').nullable(),
        content: Yup.string().required('Content is required'),
        description: Yup.string().required('Description is required'),
      })
    ),
});

const WalletTopUpOfferForm = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  // recordKey = an old record, opened read-only
  const { recordKey } = useParams();
  const isAdd = useLocation().pathname === '/addWalletTopUpOffer';
  const { navigateTo } = useContext(NavContext);

  const liveOffers = useSelector((state) => state.walletTopUpOffer.walletTopUpOffers);
  const history = useSelector((state) => state.walletTopUpOffer.walletTopUpOfferHistory);
  const oldRecord = recordKey && _.find(history, { key_name: recordKey });
  const offers = recordKey ? _.get(oldRecord, 'key_value', []) : liveOffers;
  const isLoader = useSelector((state) => state.walletTopUpOffer.isLoading);
  const rolePermissionData = useSelector((state) => state.manageRole.manageRoles);
  const isReadOnly = !!recordKey || !_.some(_.get(rolePermissionData, '[0].permissions', []), { name: MANAGE_WALLETS });
  const byId = _.keyBy(offers, getOfferId);

  useEffect(() => {
    dispatch({ type: recordKey ? FETCH_WALLET_TOP_UP_OFFER_HISTORY.REQUEST : FETCH_WALLET_TOP_UP_OFFERS.REQUEST });
  }, [recordKey]);

  const slabs = _.map(offers, toSlab);
  // a new offer replaces the live record, so it waits until every live slab has ended
  const runningUntil =
    isAdd &&
    _.max(
      _.map(
        _.filter(liveOffers, (o) => moment(o.expire_at).isAfter()),
        'expire_at'
      )
    );
  const initialValues = { offers: isAdd ? [nextSlab()] : slabs };

  const onSubmit = (values) => {
    const payload = _.map(values.offers, (slab) => {
      const original = byId[slab.id] || {};
      // datetime inputs drop seconds, so an untouched date keeps its stored value
      const keepDate = (field) =>
        slab[field] && (slab[field] === toDateTimeInput(original[field]) ? original[field] : moment(slab[field]).toISOString());
      // blank optional fields are left out so untouched legacy slabs stay unchanged
      return _.omitBy(
        {
          ...slab,
          min_value: Number(slab.min_value),
          max_value: Number(slab.max_value),
          discount_value: Number(slab.discount_value),
          max_discount: slab.discount_type === 'flat' || slab.max_discount === '' ? null : Number(slab.max_discount),
          priority: slab.priority === '' || slab.priority === null ? 1 : Number(slab.priority),
          start_date: keepDate('start_date'),
          expire_at: keepDate('expire_at'),
        },
        (v) => v === '' || v === undefined
      );
    });
    // keeps isSubmitting true until the saga cb fires
    return new Promise((resolve) => {
      dispatch({
        type: SAVE_WALLET_TOP_UP_OFFERS.REQUEST,
        payload: { offers: payload, ...(isAdd && { new_record: true }) },
        cb: (res) => {
          resolve();
          _.get(res, 'status') === UPDATE_STATUS_CODE && navigateTo('/walletTopUpOffers');
        },
      });
    });
  };

  return (
    <React.Fragment>
      <LayoutContainer>
        <AdminHeader
          title={recordKey ? `Old Record ${_.get(oldRecord, 'copy_no', '')}` : isAdd ? 'Add Offer' : 'Live Top-up Offers'}
          backTo="/walletTopUpOffers"
        />
        <div className="page-content-wrapper scrollable">
          <Card>
            <div className="p-4">
              {recordKey && <p style={mutedText}>Read-only old record ({recordKey}). The app does not use it.</p>}
              {!isReadOnly && (
                <p style={mutedText}>
                  {isAdd
                    ? 'Saving makes this the live offer with only the slabs below; the current live record moves to old records.'
                    : 'All slabs below are the live record. Changes are saved to it directly.'}
                </p>
              )}
              {runningUntil && (
                <p className="text-danger">
                  Live record is still running until {formatOfferDate(runningUntil)}. Archive it from the list with an end date that has passed, then
                  add the new offer.
                </p>
              )}
              <Formik enableReinitialize initialValues={initialValues} validationSchema={validationSchema} onSubmit={onSubmit}>
                {({ values, errors, touched, handleChange, handleSubmit, setFieldValue, isSubmitting }) => (
                  <Form onSubmit={handleSubmit}>
                    <fieldset disabled={isReadOnly}>
                      <FieldArray name="offers">
                        {({ push, remove }) => (
                          <React.Fragment>
                            {_.map(values.offers, (slab, index) => {
                              const name = (field) => `offers.${index}.${field}`;
                              const original = byId[slab.id];
                              return (
                                <div key={slab.id || `new-slab-${index}`} style={slabCard}>
                                  <div className="d-flex justify-content-between align-items-center mb-3">
                                    <div className="section-heading d-flex align-items-center mb-0" style={{ gap: 10 }}>
                                      Slab {index + 1}
                                      {original ? renderOfferStatus(getTopUpOfferStatus(original)) : <span style={mutedText}>New</span>}
                                    </div>
                                    {!isReadOnly && values.offers.length > 1 && (
                                      <Button className="user-group-delete-Btn" onClick={() => remove(index)}>
                                        Remove
                                      </Button>
                                    )}
                                  </div>
                                  <Row>
                                    <Col lg={3}>
                                      <Select
                                        label="Type"
                                        options={TYPE_OPTIONS}
                                        value={slab.type}
                                        isDisabled={isReadOnly}
                                        onChange={(val) => setFieldValue(name('type'), val)}
                                      />
                                    </Col>
                                    <Col lg={3}>
                                      <Select
                                        isRequired
                                        label="Discount Type"
                                        options={DISCOUNT_TYPE_OPTIONS}
                                        value={slab.discount_type}
                                        isDisabled={isReadOnly}
                                        onChange={(val) => {
                                          setFieldValue(name('discount_type'), val);
                                          if (val === 'flat') setFieldValue(name('max_discount'), '');
                                        }}
                                      />
                                    </Col>
                                    {_.map(FIELDS, ([field, label, lg, type, required]) => (
                                      <Col lg={lg} key={field}>
                                        <TextInput
                                          isRequired={required}
                                          type={type === 'textarea' ? undefined : type}
                                          as={type === 'textarea' ? 'textarea' : undefined}
                                          label={label}
                                          name={name(field)}
                                          value={slab[field]}
                                          min={field === 'expire_at' ? slab.start_date || undefined : undefined}
                                          disabled={field === 'max_discount' && slab.discount_type === 'flat'}
                                          onChange={handleChange}
                                          error={fieldError(errors, touched, name(field))}
                                        />
                                      </Col>
                                    ))}
                                  </Row>
                                </div>
                              );
                            })}
                            {!isReadOnly && (
                              <Button className="user-group-btn" onClick={() => push(nextSlab(_.last(values.offers)))}>
                                + Add Slab
                              </Button>
                            )}
                          </React.Fragment>
                        )}
                      </FieldArray>
                    </fieldset>

                    <div className="d-flex justify-content-end mt-4" style={{ gap: 12 }}>
                      <Button className="cancel_btn" onClick={() => navigateTo('/walletTopUpOffers')}>
                        {t('button.cancel', 'Cancel')}
                      </Button>
                      {!isReadOnly && (
                        <Button type="submit" className="save-btn" disabled={isSubmitting || isLoader || !!runningUntil}>
                          {isSubmitting ? 'Saving...' : t('button.save', 'Save')}
                        </Button>
                      )}
                    </div>
                  </Form>
                )}
              </Formik>
            </div>
          </Card>
        </div>
      </LayoutContainer>
    </React.Fragment>
  );
};

export default WalletTopUpOfferForm;
